/**
 * The signed-in person's Settings, from the top bar's profile menu.
 *
 *   Preferences  where they land, and whether GRiPS (the help assistant) shows.
 *                Edited as a draft and stored with Save changes
 *                (PATCH /api/me/preferences); they take effect once saved.
 *
 * Closing with an unsaved draft asks first: save, discard, or keep editing.
 *
 * The landing choice is shown to approvers only: everyone else lands on Home,
 * so there is nothing for them to choose. The landing logic
 * (utils/navigation.ts resolveDefaultView) also ignores a stored desk choice
 * for someone who no longer approves, so this is not the only guard.
 */
import { useEffect, useId, useState, type ComponentType, type ReactNode } from 'react';
import { AdjustmentsHorizontalIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import Modal, { ModalBody, ModalFooter } from './shared/Modal';
import Card from './shared/Card';
import Alert from './shared/Alert';
import Button from './shared/Button';
import Switch from './shared/Switch';
import { useMyProfile, type LandingPreference, type MyPreferences } from '../hooks/useMyProfile';
import { useApprovalsDeskAccess } from '../hooks/useLandingProfile';
import { TEXT } from './shared/Typography';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  token?: string;
  /** The signed-in roles, for the Approval Desk landing choice. */
  roles?: string[];
}

function Section({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
  children: ReactNode;
}) {
  const headingId = useId();
  return (
    <Card padding="none" shadow="none" className="border border-gray-200">
      <section aria-labelledby={headingId}>
        <header className="flex items-start gap-3 border-b border-gray-100 px-4 py-3 sm:px-5">
          <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-chrome-700 text-white">
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <span className="min-w-0">
            <h3 id={headingId} className={TEXT.cardTitle}>
              {title}
            </h3>
            <p className={TEXT.bodyMuted}>{description}</p>
          </span>
        </header>
        <div className="px-4 py-4 sm:px-5">{children}</div>
      </section>
    </Card>
  );
}

/** What someone lands on, whether chosen or by default. */
function effectiveLanding(p: MyPreferences, isApprover: boolean): LandingPreference {
  return p.landingPage === 'home' || !isApprover ? 'home' : 'approvals';
}

export default function SettingsModal({ isOpen, onClose, token, roles = [] }: SettingsModalProps) {
  const { profile, savePreferences } = useMyProfile(token);
  const { allowed: isApprover } = useApprovalsDeskAccess(token, roles);
  const saved = profile.preferences;
  const [draft, setDraft] = useState<MyPreferences>(saved);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const gripsLabelId = useId();
  const gripsHintId = useId();

  // Start each opening from what is stored.
  useEffect(() => {
    if (isOpen) {
      setDraft(saved);
      setStatus(null);
      setConfirmClose(false);
    }
    // Only on open: a background refresh of `saved` must not wipe an edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const landing = effectiveLanding(draft, isApprover);
  const prefsDirty =
    landing !== effectiveLanding(saved, isApprover) || draft.showGrips !== saved.showGrips;

  const edit = (patch: Partial<MyPreferences>) => {
    setStatus(null);
    setDraft(d => ({ ...d, ...patch }));
  };

  const save = async (): Promise<boolean> => {
    setSaving(true);
    setStatus(null);
    try {
      const stored = await savePreferences({ landingPage: landing, showGrips: draft.showGrips });
      setDraft(stored);
      setStatus({ type: 'success', text: 'Your settings have been saved.' });
      return true;
    } catch (err: unknown) {
      // Say what the server said: a generic message hides the cause from the
      // person and from whoever they report it to.
      const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string };
      const detail = e.response?.data?.error || e.message;
      console.error('[Settings] saving preferences failed:', e.response?.status, detail);
      setStatus({
        type: 'error',
        text: `Your settings could not be saved${detail ? `: ${detail}` : '.'}${
          e.response?.status ? ` (error ${e.response.status})` : ''
        }`,
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (prefsDirty) setConfirmClose(true);
    else onClose();
  };

  const landingOption = (value: LandingPreference, label: string, hint: string) => {
    const selected = landing === value;
    return (
      <label
        className={`relative flex min-h-[2.75rem] cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 transition-colors duration-150 ${
          selected
            ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600'
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }`}
      >
        <input
          type="radio"
          name="landing-page"
          value={value}
          checked={selected}
          disabled={saving}
          onChange={() => edit({ landingPage: value })}
          className="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600"
        />
        <span className="min-w-0">
          <span className="block text-base font-medium text-gray-900">{label}</span>
          <span className={`block ${TEXT.bodyMuted}`}>{hint}</span>
        </span>
      </label>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={requestClose} title="Settings" size="lg">
        <ModalBody className="space-y-5">
          <Section
            icon={AdjustmentsHorizontalIcon}
            title="Preferences"
            description="How GRAutomates opens and what it shows you. Saved to your account."
          >
            <div className="space-y-5">
              {isApprover && (
                <fieldset className="border-b border-gray-100 pb-5">
                  <legend className={`mb-2 ${TEXT.bodyEmphasis}`}>Landing page</legend>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {landingOption('approvals', 'Approval Desk', 'Everything waiting for your approval.')}
                    {landingOption('home', 'Home', 'Company updates, shoutouts and tips.')}
                  </div>
                </fieldset>
              )}

              <div className="flex items-center justify-between gap-4">
                <span className="min-w-0">
                  <span id={gripsLabelId} className={`block ${TEXT.bodyEmphasis}`}>
                    Show GRiPS
                  </span>
                  <span id={gripsHintId} className={`block ${TEXT.bodyMuted}`}>
                    The help assistant in the corner of every page.
                  </span>
                </span>
                <Switch
                  checked={draft.showGrips}
                  onChange={showGrips => edit({ showGrips })}
                  disabled={saving}
                  aria-labelledby={gripsLabelId}
                  aria-describedby={gripsHintId}
                />
              </div>
            </div>
          </Section>

          {status && <Alert variant={status.type} message={status.text} />}
        </ModalBody>

        <ModalFooter className="justify-between">
          <span className={TEXT.bodyMuted} aria-live="polite">
            {prefsDirty ? 'You have unsaved changes.' : ''}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={requestClose} disabled={saving}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={() => void save()}
              disabled={!prefsDirty || saving}
              icon={<CheckCircleIcon className="h-5 w-5" />}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        title="Unsaved changes"
        size="sm"
      >
        <ModalBody>
          <p className="text-base text-gray-700">You have changed your settings but not saved them.</p>
          {status?.type === 'error' && (
            <Alert variant="error" message={status.text} className="mt-3" />
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setConfirmClose(false)}>
            Keep editing
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setConfirmClose(false);
              onClose();
            }}
          >
            Discard changes
          </Button>
          <Button
            variant="primary"
            disabled={saving}
            onClick={async () => {
              if (await save()) {
                setConfirmClose(false);
                onClose();
              }
            }}
          >
            {saving ? 'Saving…' : 'Save and close'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
